import { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'

import { useGetEmployeeEntitlement } from 'services/api/modules/leave/entitlement'

import getRequestEntitlementBreakdown from 'module/leave/util/getRequestEntitlementBreakdown'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxPdfImageViewer from 'components/reusable/AdxPdfImageViewer'

import DefaultAvatar from 'assets/images/default_company_photo.png'
import AdxButton from 'components/custom/AdxButton'

const ConfirmLeaveView = ({
  formikProps,
  setPageState,
  selectedLeave,
  isSubmitting,
  setSubmitError,
}) => {
  const { data: leaveBalance } = useGetEmployeeEntitlement()
  const { values, handleSubmit } = formikProps
  const [balance, setBalance] = useState({})

  const calculateBalance = (entitlementCurrent, creditCurrent, carryForwardCurrent, duration) => {
    let entitlementBalance
    let creditBalance = creditCurrent
    let carryForwardBalance = carryForwardCurrent
    let durationRemaining = duration

    if (creditCurrent && durationRemaining > 0) {
      if (creditCurrent - durationRemaining <= 0) {
        creditBalance = 0
        durationRemaining = durationRemaining - creditCurrent
      } else {
        creditBalance = creditCurrent - durationRemaining
        durationRemaining = 0
      }
    }
    if (carryForwardCurrent && durationRemaining > 0) {
      if (carryForwardCurrent - durationRemaining <= 0) {
        carryForwardBalance = 0
        durationRemaining = durationRemaining - carryForwardCurrent
      } else {
        carryForwardBalance = carryForwardCurrent - durationRemaining
        durationRemaining = 0
      }
    }

    if (entitlementCurrent - durationRemaining <= 0) {
      entitlementBalance = 0
      durationRemaining = durationRemaining - entitlementCurrent
    } else {
      entitlementBalance = entitlementCurrent - durationRemaining
      durationRemaining = 0
    }

    return {
      creditBalance,
      carryForwardBalance,
      entitlementBalance,
    }
  }

  useEffect(() => {
    const bal = calculateBalance(
      selectedLeave?.entitlement?.balance,
      leaveBalance?.leave_credit[0]?.applicable_leave?.find(
        (uItem) => uItem.leave_policy_id === selectedLeave.leave_policy_id
      )
        ? leaveBalance?.leave_credit[0]?.leave_credit.balance
        : null,
      selectedLeave?.carry_forward?.balance ? selectedLeave?.carry_forward?.balance : null,
      values.duration
    )

    setBalance(bal)
  }, [values.duration, selectedLeave]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        minHeight: 'calc(70vh - 112px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Content here */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          px: 3,
          py: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              columnGap: 2,
            }}
          >
            <Typography width="20%" variant="body1" color="light-gray-text.main">
              Type
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 1.5,
                width: '80%',
              }}
            >
              <Typography variant="body1" color="dark-text.main">
                {selectedLeave.leave_policy.name}
              </Typography>

              {leaveBalance?.leave_credit?.applicable_leave?.find(
                (uItem) => uItem.leave_policy_id === selectedLeave.leave_policy_id
              ) && (
                <AdxStatusBar
                  sx={{ py: 0.5, borderRadius: 1 }}
                  status={{
                    text: 'Replacement credit utilizable',
                    color: 'white.main',
                    bgColor: 'alternate.main',
                  }}
                />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              columnGap: 2,
            }}
          >
            <Typography width="20%" variant="body1" color="light-gray-text.main">
              Shift
            </Typography>
            <Typography sx={{ width: '80%' }} variant="body1" color="dark-text.main">
              {values.shiftLeave !== 'Full Day'
                ? `Half Day (${values.shiftLeave})`
                : values.shiftLeave}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'start',
              width: '100%',
              columnGap: 2,
            }}
          >
            <Typography width="20%" variant="body1" color="light-gray-text.main">
              Date
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', mobile_l: 'row' },
                gap: 1,
                width: '80%',
              }}
            >
              <Typography variant="body1" color="dark-text.main">
                {values.startDate} - {values.endDate}
              </Typography>
              <Typography variant="body1" color="alternate.main">
                ({values.duration} {values.duration > 1 ? 'days' : 'day'})
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'start',
              width: '100%',
              columnGap: 2,
            }}
          >
            <Typography width="20%" variant="body1" color="light-gray-text.main">
              Reason
            </Typography>
            <Typography
              sx={{ width: '80%', wordWrap: 'break-word' }}
              variant="body1"
              color="dark-text.main"
            >
              {values.reason ? values.reason : 'N/A'}
            </Typography>
          </Box>
        </Box>
        {getRequestEntitlementBreakdown(selectedLeave, leaveBalance, values, balance)}

        {Object.keys(values.file).length !== 0 && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="body1" color="light-gray-text.main">
              Attachment
            </Typography>
            <Box
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: 'gray-background.main',
                borderRadius: '4.5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 2,
                p: 2,
              }}
            >
              <Box
                width={90}
                height={90}
                border="2px dashed"
                borderColor="gray-line.main"
                borderRadius={1}
                sx={{
                  objectFit: 'cover',
                  objectPosition: '0% 0%',
                  overflow: 'hidden',
                }}
              >
                <AdxPdfImageViewer
                  fileUrl={values.file.preview || values.file.awsUrl || DefaultAvatar}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'start',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="body1" color="dark-text.main">
                  {values.file.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <AdxButton
                    variant={'outlined'}
                    size={'tiny'}
                    onClick={() => window.open(values.file.preview || values.file.awsUrl, '_blank')}
                  >
                    View
                  </AdxButton>
                  <AdxButton
                    variant={'outlined'}
                    size={'tiny'}
                    href={values.file.preview || values.file.awsUrl}
                    target="_blank"
                    download
                  >
                    Download
                  </AdxButton>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Buttons here */}
      <Box
        sx={{
          position: 'sticky',
          bottom: '0',
          width: '100%',
          display: 'flex',
          justifyContent: { xs: 'center', md: 'end' },
          alignItems: 'center',
          bgcolor: 'primary.light',
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'grayscale.line-light',
        }}
      >
        <AdxButton
          variant={'outlined'}
          onClick={() => {
            setSubmitError('')
            setPageState('FILL_DETAILS')
          }}
          sx={{ width: { xs: '100%', md: '150px' } }}
        >
          Back
        </AdxButton>
        <AdxButton
          variant={'contained'}
          onClick={handleSubmit}
          sx={{ width: { xs: '100%', md: '300px' } }}
          isLoading={isSubmitting ? true : false}
          disabled={isSubmitting}
        >
          Confirm Request
        </AdxButton>
      </Box>
    </Box>
  )
}

export default ConfirmLeaveView
